// apps/cli/src/commands/enterprise/talents/view.ts
import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';

interface ViewFlags {
	fields?: string;
}

/** 履歷預覽為巢狀物件（後端 getFindTalentResumePreview 輸出）→ 一律 JSON（不做表格）。 */
export async function runEnterpriseTalentsView(
	ctx: { baseUrl: string; locale: string; timeoutMs: number },
	apiKey: string,
	encResumeId: string,
	flags: ViewFlags
): Promise<void> {
	const trimmed = encResumeId.trim();
	if (!trimmed) {
		throw new CliError('enc_resume_id must not be empty', ExitCode.InvalidArgument);
	}
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/talents/${encodeURIComponent(trimmed)}`
	);
	const resume = unwrapDataResponse<Record<string, unknown>>(body);
	if (flags.fields) {
		printJson(pickPaths(resume, parseFieldsList(flags.fields)));
		return;
	}
	printJson(resume);
}

export function registerEnterpriseTalentsView(parent: Command): void {
	parent
		.command('view <enc_resume_id>')
		.description('View one applicant resume preview (company-scoped; PII visibility per access level)')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.action(async (encResumeId: string, flags: ViewFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runEnterpriseTalentsView(ctx, key, encResumeId, flags);
		});
}
